import React from 'react'
import styled from 'styled-components'
import { DatePicker } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

type FieldType = 'string' | 'number' | 'boolean' | 'enum' | 'datetime' | 'array'

type FieldLeaf = {
  type: FieldType
  value: unknown
}

// Break the circular reference with an interface
interface FieldNode {
  [key: string]: FieldLeaf | FieldNode | FieldNode[] | unknown[]
}

type AnyNode = FieldLeaf | FieldNode | unknown[] | null | undefined

const isFieldLeaf = (node: unknown): node is FieldLeaf =>
  typeof node === 'object' &&
  node !== null &&
  !Array.isArray(node) &&
  'type' in node &&
  'value' in node &&
  ['string', 'number', 'boolean', 'enum', 'datetime', 'array'].includes(
    (node as Record<string, unknown>).type as string
  )

// ─── Path helpers ─────────────────────────────────────────────────────────────

const setIn = (
  obj: Record<string, unknown>,
  path: (string | number)[],
  value: unknown
): Record<string, unknown> => {
  if (path.length === 0) return obj
  const [head, ...tail] = path
  const current = (obj as Record<string | number, unknown>)[head]

  if (tail.length === 0) {
    return { ...obj, [head]: value }
  }

  if (Array.isArray(current)) {
    const arr = [...current]
    arr[tail[0] as number] = setIn(
      arr[tail[0] as number] as Record<string, unknown>,
      tail.slice(1),
      value
    )
    return { ...obj, [head]: arr }
  }

  return {
    ...obj,
    [head]: setIn((current ?? {}) as Record<string, unknown>, tail, value),
  }
}

// ─── Leaf input ───────────────────────────────────────────────────────────────

const LeafInput = ({
  field,
  path,
  onChange,
}: {
  field: FieldLeaf
  path: (string | number)[]
  onChange: (path: (string | number)[], value: unknown) => void
}) => {
  const key = path[path.length - 1]
  const label = String(key)
  const handleChange = (val: unknown) => onChange(path, val)

  if (field.type === 'boolean') {
    return (
      <LeafRow>
        <LeafLabel title={label}>{label}</LeafLabel>
        <BoolToggle>
          <input
            type="checkbox"
            checked={Boolean(field.value)}
            onChange={e => handleChange(e.target.checked)}
          />
          <BoolValue $checked={Boolean(field.value)}>
            {field.value ? 'Oui' : 'Non'}
          </BoolValue>
        </BoolToggle>
      </LeafRow>
    )
  }

  if (field.type === 'number') {
    return (
      <LeafRow>
        <LeafLabel title={label}>{label}</LeafLabel>
        <LeafInputField
          type="number"
          value={field.value == null ? '' : String(field.value)}
          onChange={e =>
            handleChange(e.target.value === '' ? null : Number(e.target.value))
          }
          $type="number"
        />
      </LeafRow>
    )
  }

  if (field.type === 'datetime') {
    const dayjsValue =
      field.value != null
        ? dayjs(field.value as string, ['YYYY-MM-DD', 'DD/MM/YYYY'], true)
        : null
    return (
      <LeafRow>
        <LeafLabel title={label}>{label}</LeafLabel>
        <DatePicker
          value={dayjsValue?.isValid() ? dayjsValue : null}
          onChange={(date: Dayjs | null) =>
            handleChange(date ? date.toISOString() : null)
          }
          format="DD/MM/YYYY"
          placeholder="JJ/MM/AAAA"
          style={{ width: '100%' }}
          size="small"
        />
      </LeafRow>
    )
  }

  return (
    <LeafRow>
      <LeafLabel title={label}>{label}</LeafLabel>
      <LeafInputField
        type="text"
        value={field.value == null ? '' : String(field.value)}
        onChange={e => handleChange(e.target.value || null)}
        $type={field.type}
      />
    </LeafRow>
  )
}

// ─── Recursive node ───────────────────────────────────────────────────────────

const ContentNode = ({
  node,
  path,
  depth,
  onChange,
}: {
  node: AnyNode
  path: (string | number)[]
  depth: number
  onChange: (path: (string | number)[], value: unknown) => void
}) => {
  if (node == null) return null

  if (isFieldLeaf(node)) {
    return <LeafInput field={node} path={path} onChange={onChange} />
  }

  if (Array.isArray(node)) {
    if (node.length === 0) return <EmptyArray>[ ]</EmptyArray>
    return (
      <ArrayBlock>
        {node.map((item, i) => (
          <ArrayItem key={i}>
            <ArrayIndex>[{i}]</ArrayIndex>
            <ContentNode
              node={item as AnyNode}
              path={[...path, i]}
              depth={depth + 1}
              onChange={onChange}
            />
          </ArrayItem>
        ))}
      </ArrayBlock>
    )
  }

  if (typeof node === 'object') {
    const entries = Object.entries(node as Record<string, AnyNode>)
    return (
      <ObjectBlock>
        {entries.map(([key, child]) => {
          if (isFieldLeaf(child)) {
            return (
              <ContentNode
                key={key}
                node={child}
                path={[...path, key]}
                depth={depth}
                onChange={onChange}
              />
            )
          }
          return (
            <Section key={key} $depth={depth}>
              <SectionHeader $depth={depth}>{key}</SectionHeader>
              <SectionBody>
                <ContentNode
                  node={child as AnyNode}
                  path={[...path, key]}
                  depth={depth + 1}
                  onChange={onChange}
                />
              </SectionBody>
            </Section>
          )
        })}
      </ObjectBlock>
    )
  }

  return null
}

// ─── Main component ───────────────────────────────────────────────────────────

type Props = {
  content: Record<string, unknown>
  onChange: (content: Record<string, unknown>) => void
}

const DepositContentEditor = ({ content, onChange }: Props) => {
  const handleChange = (path: (string | number)[], value: unknown) => {
    onChange(setIn(content, [...path, 'value'], value))
  }

  return (
    <Root>
      <ContentNode
        node={content as AnyNode}
        path={[]}
        depth={0}
        onChange={handleChange}
      />
    </Root>
  )
}

export default DepositContentEditor

// ─── Styles ───────────────────────────────────────────────────────────────────

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
`

const Section = styled.div<{ $depth: number }>`
  display: flex;
  flex-direction: column;
  border-left: 2px solid ${p => depthColor(p.$depth)};
  margin-top: 6px;
`

const SectionHeader = styled.div<{ $depth: number }>`
  font-size: 11px;
  font-weight: 700;
  color: ${p => depthColor(p.$depth)};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 3px 8px;
  background: ${p => depthBg(p.$depth)};
`

const SectionBody = styled.div`
  padding-left: 12px;
  display: flex;
  flex-direction: column;
  gap: 1px;
`

const ObjectBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`

const ArrayBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ArrayItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
`

const ArrayIndex = styled.span`
  font-size: 10px;
  color: #999;
  padding-top: 6px;
  min-width: 24px;
`

const EmptyArray = styled.span`
  font-size: 11px;
  color: #aaa;
  font-style: italic;
  padding: 4px 0;
`

const LeafRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
  min-height: 28px;
`

const LeafLabel = styled.label`
  font-size: 11px;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const LeafInputField = styled.input<{ $type: FieldType }>`
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 3px 7px;
  font-size: 12px;
  color: #171a1c;
  background: ${p => (p.$type === 'number' ? '#f6f9ff' : '#fff')};
  outline: none;
  width: 100%;
  min-width: 0;

  &:focus {
    border-color: #1677ff;
  }

  &::-webkit-inner-spin-button {
    opacity: 1;
  }
`

const BoolToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;

  input {
    width: 14px;
    height: 14px;
    cursor: pointer;
    accent-color: #1677ff;
  }
`

const BoolValue = styled.span<{ $checked: boolean }>`
  font-size: 11px;
  font-weight: 600;
  color: ${p => (p.$checked ? '#52c41a' : '#ff4d4f')};
`

// ─── Depth palette ────────────────────────────────────────────────────────────

const DEPTH_COLORS = ['#1677ff', '#7c3aed', '#0891b2', '#d97706', '#059669']
const DEPTH_BGS = ['#f0f5ff', '#f5f3ff', '#ecfeff', '#fffbeb', '#ecfdf5']

const depthColor = (d: number) => DEPTH_COLORS[d % DEPTH_COLORS.length]
const depthBg = (d: number) => DEPTH_BGS[d % DEPTH_BGS.length]
